
export default function UtilityAccessSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Why Utility Access Matters
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            "Water and power at the street" is one of the most valuable phrases in land sales. Here's why utilities can make or break your land's value.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold mb-6 text-gray-900">The Utility Premium</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Land with utilities already at the property line can sell for 50-100% more than identical land without utilities. Why? Because bringing utilities to raw land is expensive and time-consuming.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Installing water, sewer, and electric to a property can cost $15,000-$50,000 or more depending on distance from existing infrastructure. Most individual buyers can't afford these costs, which dramatically limits your buyer pool.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg mb-6">
              <h4 className="font-bold text-gray-900 mb-2">Example:</h4>
              <p className="text-gray-700 leading-relaxed">
                Two identical 1-acre lots in East El Paso. Lot A has water, sewer, and electric at the street - sells for $35,000. Lot B has no utilities and is 1/4 mile from the nearest connection - sells for $18,000. That's a $17,000 difference based solely on utility access.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <i className="ri-check-line text-teal-600 text-2xl mt-1"></i>
                <div>
                  <strong className="block text-gray-900 mb-1">Water Service</strong>
                  <p className="text-gray-700 leading-relaxed">Municipal water is ideal. Well water is acceptable but requires drilling costs ($8,000-$15,000). No water source severely limits value.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <i className="ri-check-line text-teal-600 text-2xl mt-1"></i>
                <div>
                  <strong className="block text-gray-900 mb-1">Sewer or Septic</strong>
                  <p className="text-gray-700 leading-relaxed">City sewer is best. Septic systems cost $5,000-$15,000 to install. Land that can't support septic (poor soil, high water table) is difficult to develop.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <i className="ri-check-line text-teal-600 text-2xl mt-1"></i>
                <div>
                  <strong className="block text-gray-900 mb-1">Electric Power</strong>
                  <p className="text-gray-700 leading-relaxed">Power at the street is crucial. Running new power lines costs $10-$30 per linear foot. A property 1,000 feet from power could require $10,000-$30,000 in utility costs.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full h-96 rounded-lg overflow-hidden shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/638236d1723e1847688530df8a20f855.jpg"
              alt="Land utility access and infrastructure"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'https://picsum.photos/seed/utility-land/800/600.jpg';
              }}
            />
          </div>
        </div>

        <div className="mb-16">
          <h3 className="text-3xl font-bold mb-8 text-gray-900">Off-Grid Land: Challenges and Opportunities</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-lg border-2 border-gray-200">
              <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-lg mb-6">
                <i className="ri-close-circle-line text-red-600 text-3xl"></i>
              </div>
              <h4 className="text-2xl font-bold mb-4 text-gray-900">The Challenges</h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                Off-grid land (no utilities) appeals to a very small buyer segment - mostly homesteaders, preppers, or recreational users. This limited demand means lower prices and longer selling times.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Banks won't finance off-grid land purchases, eliminating 90% of potential buyers. Only cash buyers can purchase, and they'll demand steep discounts.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Development costs are prohibitive for most buyers. Solar power systems ($15,000-$40,000), well drilling ($8,000-$15,000), and septic installation ($5,000-$15,000) can total $30,000-$70,000 before building even starts.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg border-2 border-teal-600">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                <i className="ri-check-circle-line text-teal-600 text-3xl"></i>
              </div>
              <h4 className="text-2xl font-bold mb-4 text-teal-600">We Buy Off-Grid Land</h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                At TRex RE LLC, we specialize in buying land that other buyers won't touch. Off-grid properties are our specialty because we understand their value and have the resources to develop them or hold them long-term.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                We don't need financing, so lack of utilities doesn't stop us from making offers. We evaluate off-grid land based on its potential, location, and our investment criteria - not traditional lending standards.
              </p>
              <p className="text-gray-700 leading-relaxed">
                If you own remote acreage with no utilities, don't assume it's worthless. Call us for a fair cash offer. We've purchased dozens of off-grid properties and can close quickly.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-8 text-white">
          <h3 className="text-2xl font-bold mb-6">How to Find Out What Utilities Are Available</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold mb-3 text-teal-400">Contact Utility Providers</h4>
              <ul className="space-y-2 text-gray-300">
                <li>• El Paso Water: (915) 594-5500</li>
                <li>• El Paso Electric: (915) 543-5970</li>
                <li>• Texas Gas Service: (800) 700-2443</li>
              </ul>
              <p className="text-gray-300 mt-4 leading-relaxed">
                Provide your property address or parcel number, and they'll tell you if service is available and connection costs.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-3 text-teal-400">Or Let Us Handle It</h4>
              <p className="text-gray-300 leading-relaxed mb-4">
                When you contact TRex RE LLC for a cash offer, we'll research utility availability as part of our due diligence. You don't need to call utility companies or figure out connection costs - we handle all of that.
              </p>
              <a href="tel:+1-915-206-5814" className="inline-flex items-center space-x-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-phone-fill"></i>
                <span>Get Your Cash Offer</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
